import { Injectable } from '@angular/core';
import { InputAppDataService } from '@monorepo/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import produce from 'immer';
import { from, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { PersistentStorageService } from '../../persistent-storage/persistent-storage.service';
import { InputAppClientDBFacade } from '../input-app-client-db/input-app-client-db.facade';
import {
  AcquireLockAndSyncIncompleteBundles,
  BundleQcDBActionTypes,
  BundleQcNoAction,
  ReleaseBundleQcBgDBLock,
  SyncIncompleteBundlesAndReleaseLock,
  UpdateBundleQcBgDBAndReleaseLock,
} from './bundle-qc.actions';
import { BundleQcDBFacade } from './bundle-qc.facade';
import { getBundleQcInitialState } from './bundle-qc.reducer';

@Injectable({
  providedIn: 'root',
})
export class BundleQcDBEffects {
  constructor(
    private action$: Actions,
    private persistentStorageService: PersistentStorageService,
    private bundleQcFacade: BundleQcDBFacade,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private inputAppDataService: InputAppDataService,
  ) {}
  AcquireLockAndSyncIncompleteBundles$ = createEffect(() =>
    this.action$.pipe(
      ofType<AcquireLockAndSyncIncompleteBundles>(BundleQcDBActionTypes.AcquireLockAndSyncIncompleteBundles),
      switchMap((action) => {
        return this.bundleQcFacade.acquireBgDBLock('SYNCING_COMPLETE_BUNDLES').pipe(
          map((res) => {
            if (res.status === 'SUCCESS') {
              return new SyncIncompleteBundlesAndReleaseLock(action.stationDetails, action.chainAction);
            } else {
              return new BundleQcNoAction();
            }
          }),
        );
      }),
    ),
  );

  SyncIncompleteBundlesAndReleaseLock$ = createEffect(() =>
    this.action$.pipe(
      ofType<SyncIncompleteBundlesAndReleaseLock>(BundleQcDBActionTypes.SyncIncompleteBundlesAndReleaseLock),
      switchMap((action) =>
        this.inputAppClientDBFacade
          .getIncompleteBundlesInfoForCustomLineAndShift(action.stationDetails.subject, action.stationDetails.shiftId)
          .pipe(
            map((bundlesInfo) => {
              return [bundlesInfo, action.stationDetails, action] as const;
            }),
          ),
      ),
      switchMap(([bundlesInfo, stationDetails, action]) =>
        from(this.persistentStorageService.get('[APP][INPUT_APP_BUNDLE_QC_BACKGROUND_DB]')).pipe(
          map((bgDB) => {
            return [bundlesInfo, bgDB || getBundleQcInitialState().bundleQcBgDB, stationDetails, action] as const;
          }),
        ),
      ),
      switchMap(([bundlesInfo, bgDB, stationDetails, action]) => {
        return this.inputAppDataService
          .updateIncompleteBundles({
            data: bundlesInfo.list,
            app_type: stationDetails.hsk,
            domerole: stationDetails.hsk,
            sub_id: stationDetails.subject,
            subject_key: stationDetails.subjectKey,
            hsk: stationDetails.hsk,
          })
          .pipe(
            take(1),
            switchMap(() => {
              const updatedBgDB = produce(bgDB, (draft) => {
                if (!draft[stationDetails.subject]) {
                  draft[stationDetails.subject] = {
                    [stationDetails.shiftId]: {
                      lastSyncedAt: bundlesInfo.lastUpdatedAt,
                    },
                  };
                } else {
                  draft[stationDetails.subject][stationDetails.shiftId] = {
                    lastSyncedAt: bundlesInfo.lastUpdatedAt,
                  };
                }
              });
              return from(
                this.persistentStorageService.set('[APP][INPUT_APP_BUNDLE_QC_BACKGROUND_DB]', updatedBgDB),
              ).pipe(
                take(1),
                map(() => {
                  return new UpdateBundleQcBgDBAndReleaseLock(updatedBgDB, action.chainAction);
                }),
              );
            }),
            catchError(() => {
              return of(new ReleaseBundleQcBgDBLock());
            }),
          );
      }),
    ),
  );

  UpdateBundleQcBgDBAndReleaseLock$ = createEffect(() =>
    this.action$.pipe(
      ofType<UpdateBundleQcBgDBAndReleaseLock>(BundleQcDBActionTypes.UpdateBundleQcBgDBAndReleaseLock),
      switchMap((action) => {
        if (action.chainAction.chainWithAppUpdate)
          this.bundleQcFacade.emitAppUpdateSubscription({
            syncResult: {
              status: 'ALL_SYNCED',
            },
            remoteDeploymentcheck: action.chainAction.chainWithAppUpdate,
          });
        return of(new BundleQcNoAction());
      }),
    ),
  );
}
